import { useEffect, useState } from 'react'
import {
  Badge,
  Button,
  MarkdownEditor,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@duck/ui'
import { EllipsisVertical, Pencil, Trash2, Power, PowerOff } from 'lucide-react'
import { useTar } from '@/modules/i18n/useTar'
import type { UserService } from '@duck/api-definitions'
import {
  getLabels,
  type ReleaseNoteLabel,
  LABEL_BORDER_COLORS,
  LABEL_BADGE_CLASSES,
  LABEL_ICONS,
  LABEL_I18N_KEYS,
} from './releaseNoteLabels'

export { getLabels }

function getPrimaryLabel(note: UserService.ReleaseNote): ReleaseNoteLabel {
  const labels = getLabels(note)
  const priority: ReleaseNoteLabel[] = [
    'admin',
    'partner',
    'announcement',
    'ai',
    'feature',
    'bugfix',
    'improvement',
    'challenge',
    'comingsoon',
  ]
  for (const label of priority) {
    if (labels.includes(label)) return label
  }
  return labels[0] ?? 'feature'
}

export const UNREAD_GLOW_DURATION_MS = 3000
const UNREAD_GLOW_FADE_OUT_DURATION_MS = 2000

export interface ReleaseNoteProps {
  note: UserService.ReleaseNote
  isAdmin: boolean
  onEdit: (note: UserService.ReleaseNote) => void
  onDelete: (id: string) => void
  onToggleActive: (note: UserService.ReleaseNote) => void
}

export const ReleaseNote = ({ note, isAdmin, onEdit, onDelete, onToggleActive }: ReleaseNoteProps) => {
  const { t } = useTar()
  const [showUnreadGlow, setShowUnreadGlow] = useState(() => !!note.unread)
  const [isFadingOut, setIsFadingOut] = useState(false)

  useEffect(() => {
    if (!note.unread || !showUnreadGlow) return
    const startFadeId = window.setTimeout(() => setIsFadingOut(true), UNREAD_GLOW_DURATION_MS)
    return () => window.clearTimeout(startFadeId)
  }, [note.unread, showUnreadGlow])

  useEffect(() => {
    if (!isFadingOut) return
    const clearGlowId = window.setTimeout(() => {
      setShowUnreadGlow(false)
      setIsFadingOut(false)
    }, UNREAD_GLOW_FADE_OUT_DURATION_MS)
    return () => window.clearTimeout(clearGlowId)
  }, [isFadingOut])

  const primaryLabel = getPrimaryLabel(note)
  const labels = getLabels(note)
  const isInactive = isAdmin && note.active === false

  const overlayOpacity = showUnreadGlow ? (isFadingOut ? 0 : 1) : 0

  return (
    <article
      id={note.slug ?? note.id}
      data-testid="release-note-card"
      className={cn(
        'relative rounded-xl border border-border border-s-[3px] bg-card text-card-foreground shadow-sm',
        LABEL_BORDER_COLORS[primaryLabel] ?? 'border-s-primary',
        isInactive && 'opacity-70'
      )}
    >
      {showUnreadGlow && (
        <div
          className="pointer-events-none absolute inset-0 rounded-xl border-2 border-s-0 border-primary/40"
          style={{
            boxShadow: '0 0 24px hsl(var(--color-primary) / 0.2), 0 0 24px hsl(var(--color-primary) / 0.08)',
            opacity: overlayOpacity,
            transition: `opacity ${UNREAD_GLOW_FADE_OUT_DURATION_MS}ms ease-out`,
          }}
          aria-hidden
        />
      )}
      <div className="relative z-10 p-6">
        <div className="flex items-start justify-between gap-3 mb-6">
          <h2 className="text-xl font-semibold text-foreground leading-tight">
            {note.title}
          </h2>
          <div className="flex flex-wrap gap-1 justify-end items-center">
            {isAdmin && isInactive && (
              <Badge variant="secondary" className="text-[10px]">
                {t('releaseNotes.inactive')}
              </Badge>
            )}
            {labels.map((label) => (
              <Badge
                key={label}
                className={cn(
                  'shrink-0 uppercase text-[10px] font-semibold tracking-wide flex items-center gap-1',
                  LABEL_BADGE_CLASSES[label]
                )}
              >
                <span className="inline-flex items-center justify-center">
                  {LABEL_ICONS[label]}
                </span>
                <span>
                  {t(LABEL_I18N_KEYS[label])}
                </span>
              </Badge>
            ))}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                    <EllipsisVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(note)}>
                    <Pencil className="h-4 w-4 mr-2" />
                    {t('common.edit')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggleActive(note)}>
                    {(note.active ?? true) ? (
                      <>
                        <PowerOff className="h-4 w-4 mr-2" />
                        {t('releaseNotes.deactivate')}
                      </>
                    ) : (
                      <>
                        <Power className="h-4 w-4 mr-2" />
                        {t('releaseNotes.activate')}
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => onDelete(note.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.delete')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        <MarkdownEditor
          defaultValue={note.body}
          mode="view"
          noBorder
          mdClassName='p-0'
        />
      </div>
    </article>
  )
}
